import { useId } from "react";
import { useTheme } from "../../hooks/useTheme.jsx";
import { cn } from "../../lib/utils.js";

/**
 * Selector de tema — control segmentado de TRES opciones.
 *
 * Tres y no dos porque el estado guardado tiene tres valores (ver
 * `useTheme.jsx`): "Sistema" no es un punto medio entre claro y oscuro, es
 * "seguí al SO y cambiá conmigo". Un interruptor de dos posiciones obligaría a
 * elegir un tema fijo para siempre, que es justo lo que la mayoría NO quiere:
 * el teléfono ya entra en modo noche por horario y la app debería acompañarlo.
 *
 * Va con radios nativos, no con <button>: el grupo de radio ya trae gratis el
 * recorrido con flechas, el rol correcto y el anuncio "2 de 3" del lector de
 * pantalla. Los inputs están visualmente ocultos pero SIGUEN siendo el control
 * que recibe el foco — de ahí que el anillo de foco se pinte con
 * `peer-focus-visible` sobre la etiqueta.
 *
 * @param {object}  props
 * @param {string}  [props.labelledBy] - id del rótulo externo (Field usa `labelId`;
 *                                       un <label for> sobre un fieldset no asocia).
 * @param {boolean} [props.compact]    - variante para el menú de usuario.
 */

const OPTIONS = [
  { value: "system", label: "Sistema", icon: "computer" },
  { value: "light", label: "Claro", icon: "light_mode" },
  { value: "dark", label: "Oscuro", icon: "dark_mode" },
];

export default function ThemeToggle({ labelledBy, compact = false, className }) {
  const { theme, setTheme } = useTheme();
  const name = useId();

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : "Tema de la interfaz"}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-lg border border-border bg-background-dark shadow-well p-0.5",
        compact ? "w-full" : "",
        className,
      )}
    >
      {OPTIONS.map((opt) => {
        const checked = theme === opt.value;
        const id = `${name}-${opt.value}`;
        return (
          <div key={opt.value} className={cn("relative", compact && "flex-1")}>
            {/*
              El radio se esconde TAPANDO su etiqueta (absolute inset-0 +
              opacity-0), no con `sr-only`.
              `sr-only` lo saca del flujo a una caja de 1px en otra posición, y
              al elegir un tema el radio recibe foco: el navegador entonces
              scrollea TODOS los ancestros scrolleables para traer esa caja a la
              vista — incluido el <main> del shell, que es `overflow-hidden`.
              Ese main se puede scrollear por programa pero no tiene barra, así
              que quedaba clavado ~362px arriba y el contenido aparecía cortado
              por arriba, sin forma de volver. Medido: enfocar el radio movía su
              scrollTop de 0 a 361.6.
              Tapando la etiqueta, el elemento enfocado YA está donde se lo ve y
              el scroll-into-view del navegador no tiene nada que corregir.
              Sigue siendo un radio nativo de verdad: foco, flechas y lector de
              pantalla intactos.
            */}
            <input
              type="radio"
              id={id}
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => setTheme(opt.value)}
              className="peer absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0"
            />
            <label
              htmlFor={id}
              className={cn(
                "flex items-center justify-center gap-1.5 rounded-md cursor-pointer select-none transition-colors",
                "peer-focus-visible:ring-2 peer-focus-visible:ring-primary/50 peer-focus-visible:outline-none",
                compact ? "px-2 py-1.5 w-full" : "px-3 py-1.5",
                // La pastilla es el "pulgar" del control segmentado: se lee
                // como una pieza levantada sobre la pista, no como un cambio
                // de tinte. Por eso lleva relleno + sombra + peso, y no solo
                // color: la selección no puede depender de un único canal.
                //
                // `surface-1` y no `surface-2`: contra la pista
                // (`background-dark`) surface-2 daba ΔL* 22.1 en oscuro pero
                // 2.2 en claro — o sea, en claro la opción elegida casi no se
                // distinguía de las otras dos. surface-1 da 11.4 y 7.1: bien
                // visible en los dos temas. En claro además es blanco puro
                // sobre pista gris, que es la forma canónica de esto.
                checked
                  ? "bg-surface-1 sheen-control text-text-primary font-medium shadow-control"
                  : "text-text-muted hover:text-text-secondary hover:bg-surface-1/60",
              )}
            >
              <span
                className="material-symbols-outlined text-[16px]"
                aria-hidden="true"
              >
                {opt.icon}
              </span>
              <span className={compact ? "text-xs" : "text-sm"}>{opt.label}</span>
            </label>
          </div>
        );
      })}
    </div>
  );
}
